import { call } from '@apparel/core/utils';
import ButtonBase from '@material-ui/core/ButtonBase';
import React, { Children, useState } from 'react';
import LightBox from 'react-images';
import { getImageSource } from './utlis/getImageSource';
import { ImageObject } from './utlis/types';

interface LightboxProps {
    images: any | string[] | File[] | ImageObject[];
    renderComponent?: any;
    children?: any;
    onClose?: () => any;
    onClickNext?: (id: number) => any;
    onClickPrev?: (id: number) => any;
    onClickThumbnail?: (id: number) => any;
    backdropClosesModal?: boolean;
    currentImage?: number;
    customControls?: any[];
    enableKeyboardInput?: boolean;
    imageCountSeparator?: string;
    isOpen?: boolean;
    preloadNextImage?: boolean;
    showCloseButton?: boolean;
    showImageCount?: boolean;
    showThumbnails?: boolean;
    theme?: Record<string, any>;
    width?: number;
}

const blobStore: any[] = [];

/**
 * @param src
 * @param isObject
 * @returns {*}
 */
const addSrc = ([src, isObject]) => {
    if (isObject) {
        blobStore.push(src as any);
    }
    return src;
};

/**
 * @example
 * const images = [
 *     createPlaceholder('256x256/fff'),
 *     createPlaceholder('156x156/fff'),
 *     createPlaceholder('126x156/fff'),
 *     createPlaceholder('256x256/fff'),
 *     createPlaceholder('256x256/fff')
 * ];
 * // Usage with custom render component
 * <Lightbox images={ images } renderComponent={ ( i ) => ( <img src={ i.source } alt={ i.caption || i.source }/> ) }/>
 *
 * // Usage with default render component `Image`
 * <Lightbox images={ images }/>
 * // More information at https://github.com/jossmac/react-images
 *
 * @param props
 * @returns {*}
 * @constructor
 */
export const Lightbox = (props: LightboxProps) => {
    const {
        children,
        renderComponent,
        images,
        currentImage,
        onClose,
        onClickNext,
        onClickPrev,
        onClickThumbnail,
        ...rest
    } = props;

    let children$ = children;
    const current$ = parseInt(`${currentImage}`);

    const [current, setCurrent] = useState(current$ || 0);
    const [open, setOpen] = useState(false);

    const images$ = ((images || []) as any).map((image) => ({
        src: addSrc(getImageSource(image, 'original')),
        thumbnail: addSrc(getImageSource(image, 'thumbnail')),
        caption: image ? image.file_name : '',
        alt: image ? image.alt : '',
    }));

    if (!children$) {
        const Component = renderComponent || Image;
        children$ = images$.map((image, idx) => {
            image.source = image.thumbnail || image.src;

            return (
                <ButtonBase key={idx}>
                    <Component {...image} />
                </ButtonBase>
            );
        });
    }

    const handleClose = () => {
        setOpen(false);
        blobStore.forEach((value) => URL.revokeObjectURL(value));
        call(onClose);
    };

    const mapChild = (child, idx) => {
        const { index, onClick, ...rest } = child.props;
        rest.onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
            setCurrent(current$ >= 0 ? current$ : index >= 0 ? index : idx);
            if (onClick && typeof onClick === 'function') {
                return onClick(e);
            }
            return false;
        };

        return {
            ...child,
            props: rest,
        };
    };

    const len = images$.length - 1;

    const handleChange = (idx, func) => {
        call(setCurrent, idx);
        call(func, idx);
    };

    const handleNext = () => handleChange(Math.min(len, current + 1), onClickNext);
    const handlePrev = () => handleChange(Math.max(0, current - 1), onClickPrev);
    const handleClickThumb = (id) => handleChange(id, onClickThumbnail);

    return (
        <>
            {Children.map(Children.toArray(children$), mapChild)}
            <LightBox
                images={images$}
                isOpen={open}
                currentImage={current}
                onClose={handleClose}
                onClickNext={handleNext}
                onClickPrev={handlePrev}
                onClickThumbnail={handleClickThumb}
                showThumbnails={true}
                {...rest}
            />
        </>
    );
};

Lightbox.displayName = 'Lightbox';
